import {useChangeLocale} from '@common/locale-switcher/change-locale';
import {
  useActiveCountry,
  useChangeCountry,
  useSiteCountries,
} from '@common/regions/country-switcher';
import {
  ColorSchemeContext,
  ColorSchemePreference,
} from '@common/core/color-scheme-provider';
import {Button} from '@shadcn/button/button';
import {Dropdown} from '@shadcn/dropdown/dropdown';
import {useBootstrapDataStore} from '@ui/bootstrap-data/bootstrap-data-store';
import {message} from '@ui/i18n/message';
import {useSelectedLocale} from '@ui/i18n/selected-locale';
import {Trans} from '@ui/i18n/trans';
import {useTrans} from '@ui/i18n/use-trans';
import {useSettings} from '@ui/settings/use-settings';
import {cn} from '@ui/utils/cn';
import {
  CheckIcon,
  ChevronDownIcon,
  GlobeIcon,
  MonitorIcon,
  MoonIcon,
  SunIcon,
} from 'lucide-react';
import {use} from 'react';

const themes: {
  value: ColorSchemePreference;
  icon: typeof SunIcon;
  label: string;
}[] = [
  {value: 'light', icon: SunIcon, label: 'Light'},
  {value: 'dark', icon: MoonIcon, label: 'Dark'},
  {value: 'system', icon: MonitorIcon, label: 'System'},
];

function flagFor(code: string) {
  const value = code.toUpperCase();
  if (value === 'EU') return '🇪🇺';
  if (!/^[A-Z]{2}$/.test(value)) return '🌐';
  return String.fromCodePoint(
    ...[...value].map(letter => 0x1f1e6 + letter.charCodeAt(0) - 65),
  );
}

const NATIVE_LANGUAGE: Record<string, {label: string; dir: 'ltr' | 'rtl'}> = {
  en: {label: 'English', dir: 'ltr'},
  ar: {label: 'العربية', dir: 'rtl'},
  fr: {label: 'Français', dir: 'ltr'},
  de: {label: 'Deutsch', dir: 'ltr'},
  es: {label: 'Español', dir: 'ltr'},
  it: {label: 'Italiano', dir: 'ltr'},
  pt: {label: 'Português', dir: 'ltr'},
  hi: {label: 'हिन्दी', dir: 'ltr'},
  ur: {label: 'اردو', dir: 'rtl'},
  tr: {label: 'Türkçe', dir: 'ltr'},
  ru: {label: 'Русский', dir: 'ltr'},
  zh: {label: '中文', dir: 'ltr'},
  ja: {label: '日本語', dir: 'ltr'},
  ko: {label: '한국어', dir: 'ltr'},
};

function languageLabel(code: string, fallback: string) {
  return NATIVE_LANGUAGE[code.toLowerCase()]?.label || fallback;
}

function languageDir(code: string): 'ltr' | 'rtl' {
  return NATIVE_LANGUAGE[code.toLowerCase()]?.dir || 'ltr';
}

export function SitePreferences({compact = false}: {compact?: boolean}) {
  const {localeCode} = useSelectedLocale();
  const siteLocales = useBootstrapDataStore(s => s.data.i18n.locales);
  const {i18n, themes: themeSettings} = useSettings();
  // Multi-countries off (default) → no countries → no country switcher.
  const countries = useSiteCountries();
  const showCountries = countries.length > 0;

  const activeLanguage = siteLocales?.find(locale => locale.language === localeCode);

  return (
    <div className={cn('flex items-center', compact ? 'gap-0' : 'gap-0.5')}>
      {showCountries ? <CountryMenu compact={compact} /> : null}
      {i18n?.enable && siteLocales?.length ? (
        <LanguageMenu
          compact={compact}
          label={languageLabel(localeCode || 'en', activeLanguage?.name || localeCode || '')}
          code={localeCode || 'en'}
        />
      ) : null}
      {themeSettings?.user_change ? <AppearanceMenu compact={compact} /> : null}
    </div>
  );
}

function CountryMenu({compact}: {compact: boolean}) {
  const {trans} = useTrans();
  const countries = useSiteCountries();
  const active = useActiveCountry();
  const change = useChangeCountry();
  if (!countries.length) return null;

  return (
    <Dropdown.Root>
      <Dropdown.Trigger
        render={
          <Button
            variant="ghost"
            size="sm"
            className="gap-1.5 px-2"
            aria-label={trans(message('Country'))}
            disabled={change.isPending}
          />
        }
      >
        {active ? (
          <span className="text-base leading-none" aria-hidden>
            {flagFor(active.code)}
          </span>
        ) : (
          <GlobeIcon className="size-4" aria-hidden />
        )}
        {compact ? null : (
          <span className="max-w-28 truncate text-sm font-medium">
            {active ? active.name : <Trans message="Global" />}
          </span>
        )}
        <ChevronDownIcon className="size-3.5 text-muted-foreground" />
      </Dropdown.Trigger>
      <Dropdown.Content align="end" className="min-w-56">
        <Dropdown.Group>
          <Dropdown.GroupLabel>
            <Trans message="Country" />
          </Dropdown.GroupLabel>
          <Dropdown.Item
            className="whitespace-nowrap"
            disabled={change.isPending}
            onClick={() => {
              if (active) change.mutate(null);
            }}
          >
            <GlobeIcon className="size-4" aria-hidden />
            <span className="min-w-0 flex-1 truncate">
              <Trans message="Global" />
            </span>
            {!active ? <CheckIcon className="ms-auto size-4 text-primary" /> : null}
          </Dropdown.Item>
          {countries.map(item => (
            <Dropdown.Item
              key={item.code}
              className="whitespace-nowrap"
              disabled={change.isPending}
              onClick={() => {
                if (item.code !== active?.code) change.mutate(item.code);
              }}
            >
              <span className="text-base leading-none" aria-hidden>
                {flagFor(item.code)}
              </span>
              <span className="min-w-0 flex-1 truncate">{item.name}</span>
              {item.code === active?.code ? (
                <CheckIcon className="ms-auto size-4 text-primary" />
              ) : null}
            </Dropdown.Item>
          ))}
        </Dropdown.Group>
      </Dropdown.Content>
    </Dropdown.Root>
  );
}

function LanguageMenu({
  compact,
  label,
  code,
}: {
  compact: boolean;
  label: string;
  code: string;
}) {
  const {trans} = useTrans();
  const siteLocales = useBootstrapDataStore(s => s.data.i18n.locales) || [];
  const changeLocale = useChangeLocale();

  return (
    <Dropdown.Root>
      <Dropdown.Trigger
        render={
          <Button
            variant="ghost"
            size="sm"
            className="gap-1 px-2"
            aria-label={trans(message('Language'))}
            disabled={changeLocale.isPending}
          />
        }
      >
        <span className="text-sm font-medium" dir={languageDir(code)}>
          {compact ? code.toUpperCase() : label}
        </span>
        <ChevronDownIcon className="size-3.5 text-muted-foreground" />
      </Dropdown.Trigger>
      <Dropdown.Content align="end" className="min-w-44">
        <Dropdown.Group>
          <Dropdown.GroupLabel>
            <Trans message="Language" />
          </Dropdown.GroupLabel>
          {siteLocales.map(locale => {
            const itemDir = languageDir(locale.language);
            const itemLabel = languageLabel(locale.language, locale.name);
            return (
              <Dropdown.Item
                key={locale.language}
                className="whitespace-nowrap"
                disabled={changeLocale.isPending}
                onClick={() => {
                  if (locale.language !== code) {
                    changeLocale.mutate({locale: locale.language});
                  }
                }}
              >
                <span
                  className={cn('min-w-0 flex-1 truncate', itemDir === 'rtl' && 'text-end')}
                  dir={itemDir}
                  lang={locale.language}
                >
                  {itemLabel}
                </span>
                {locale.language === code ? (
                  <CheckIcon className="ms-auto size-4 text-primary" />
                ) : null}
              </Dropdown.Item>
            );
          })}
        </Dropdown.Group>
      </Dropdown.Content>
    </Dropdown.Root>
  );
}

function AppearanceMenu({compact}: {compact: boolean}) {
  const {trans} = useTrans();
  const {preference, setColorScheme, colorScheme} = use(ColorSchemeContext);
  const Icon = colorScheme === 'dark' ? MoonIcon : SunIcon;

  return (
    <Dropdown.Root>
      <Dropdown.Trigger
        render={
          <Button
            variant="ghost"
            size="sm"
            className="px-2"
            aria-label={trans(message('Appearance'))}
          />
        }
      >
        <Icon className="size-4" />
        {compact ? null : <ChevronDownIcon className="size-3.5 text-muted-foreground" />}
      </Dropdown.Trigger>
      <Dropdown.Content align="end" className="min-w-40">
        <Dropdown.Group>
          <Dropdown.GroupLabel>
            <Trans message="Appearance" />
          </Dropdown.GroupLabel>
          {themes.map(theme => {
            const ThemeIcon = theme.icon;
            return (
              <Dropdown.Item
                key={theme.value}
                onClick={() => setColorScheme(theme.value)}
              >
                <ThemeIcon className="size-4" />
                <Trans message={theme.label} />
                {preference === theme.value ? (
                  <CheckIcon className="ms-auto size-4 text-primary" />
                ) : null}
              </Dropdown.Item>
            );
          })}
        </Dropdown.Group>
      </Dropdown.Content>
    </Dropdown.Root>
  );
}
